import { Injectable } from "@angular/core";
import { BackendQuestion, Question } from "../classes/dynamic-form";
import { QuestionService } from "./question.service";
import { catchError, delay, forkJoin, map, Observable, of, switchMap, tap } from "rxjs";


@Injectable( { providedIn: 'root' } )
export class DynamicFormEditorService {


	private formId = "";
	private questionMap = new Map<string, Question>();
	private questionTree: Question[] = [];
	

	constructor(
		private questionService: QuestionService
	) {}


	initialiseFormBuilder( formId="", questions: Question[]=[] ): Question[] {

		this.formId = formId;

		this.questionMap = new Map<string, Question>();
		this.questionTree = [];

		for( const question of questions ) {

			if( this.questionMap.has( question.id ) ) {
				console.warn( "!!! this question id already exists !!! not unique ?!", question.id );
				continue;
			}
			else {
				this.questionMap.set( question.id, question );
			}

			if( !question.parentId ) {
				this.questionTree.push( question );
			}

		}

		for( const question of this.questionTree ) {
			this.findAndAttachChildren( question, questions );
		}

		return this.questionTree;

	}

	private findAndAttachChildren( question: Question, questions: Question[] ) {

		for( const q of questions ) {
			if( q.id == question.id ) continue;
			if( q.parentId == question.id ) {
				question.children.push( q );
				this.findAndAttachChildren( q, questions );
			}
		}

	}

	generateQuestion( parentId="" ): Observable<string|null> {

		if( parentId ) {
			const parent = this.questionMap.get( parentId );
			if( !parent ) {
				console.warn( "addQuestion parent Id not found", parentId );
				return of(null);
			}
		}

		const question = new Question();
		question.parentId = parentId;

		return this.questionService.createQuestion( question ).pipe( 
			switchMap( questionId => {

				if( !questionId ) return of(false);

				question.id = questionId;

				return this.questionService.assignQuestionToForm( question, this.formId ).pipe(
					tap( b => {

						if( !b ) return;

						if( parentId ) {

							const parent = this.questionMap.get( parentId );
							if( !parent ) {
								console.warn( "addQuestion parent Id not found", parentId );
							}
							else {
								parent.children.push( question );
							}

						}
						else {
							this.questionTree.push( question );
						}
						this.questionMap.set( question.id, question );

					})
				);
		
			}),
			map( b => {

				if( b ) {

					setTimeout( () => {
						const elem = window.document.getElementById( "qBuilder_" + question.id );
						if( elem ) {
							elem.scrollIntoView( { behavior: "smooth", block: "center" } );
						}
					}, 300 );
	
				}

				return b ? question.id : null;
			})
		);

	}

	deleteQuestionAndKids( questionId: string ): Observable<boolean> {

		const question = this.questionMap.get( questionId );
		if( !question ) {
			console.warn( "deleteQuestion id not found", questionId, this.questionMap );
			return of(false);
		}

		const idsWithoutChildren = this.findKidsWithoutKidsRecursively( question );
		if( idsWithoutChildren.length ) {

			const obss = [];
			for( const idToDelete of idsWithoutChildren ) {
				obss.push( this.unassignAndDeleteQuestion( idToDelete ) );
			}
			return forkJoin( obss ).pipe( 
				switchMap( results => {
					for( const result of results ) {
						if( !result ) return of(false);
					}
					return this.deleteQuestionAndKids( questionId );
				}),
				catchError( e => of(false) )
			);

		}
		else return this.unassignAndDeleteQuestion( question.id );

	}

	private findKidsWithoutKidsRecursively( question: Question ): string[] {

		if( !question.children || !question.children.length ) return [];

		const idsWithoutChildren: string[] = [];
		for( const child of question.children ) {
			if( !child.children || !child.children.length ) {
				idsWithoutChildren.push( child.id );
			}
			else {
				idsWithoutChildren.push( ...this.findKidsWithoutKidsRecursively(child) );
			}
		}
		return idsWithoutChildren;

	}

	private unassignAndDeleteQuestion( questionId: string ): Observable<boolean> {

		const question = this.questionMap.get( questionId );
		if( !question ) {
			console.warn( "deleteQuestion id not found", questionId, this.questionMap );
			return of(false);
		}

		return this.questionService.unassignQuestionFromForm( questionId, this.formId ).pipe(
			switchMap( b => {

				if( !b ) return of(false);

				return this.questionService.deleteQuestion( questionId ).pipe( 
		
					map( b => {
		
						if( !b ) return false;
							
						let children = this.questionTree;
						if( question.parentId ) {
							const parent = this.questionMap.get( question.parentId );
							if( !parent ) {
								console.warn( "PARENT NOT FOUND", question.parentId, question );
								return false;
							}
							children = parent.children;
						}
						if( !children ) {
							console.warn( "BUG !children", question, question.parentId, this.questionTree );
							return false;
						}
						const i = children.findIndex( q => q.id == question.id );
						if( i == -1 ) {
							console.warn( "BUG !index", children, question, question.id, question.parentId );
							return false;
						}
						children.splice( i, 1 );
		
						this.questionMap.delete( questionId );

						return true;
		
					})
		
				);

			}),
			catchError( e => of(false) )
		);

	}

	deleteFormAndQuestions( formId: string ): Observable<boolean> {

		return this.questionService.getForm( formId ).pipe( 
			switchMap( form => {

				if( !form ) return of(false);
				
				this.initialiseFormBuilder( formId, form.questions );

				if( !form.questions.length ) return of(true);

				const obss = [];
				const topQuestions = form.questions.filter( q => !q.parentId );
				if( !topQuestions.length ) {
					console.warn( "BUG no root questions", form );
					return of(false);
				}
				for( const question of topQuestions ) {
					obss.push( this.deleteQuestionAndKids( question.id ) )
				}

				return forkJoin( obss ).pipe(
					map( results => {
						for( const result of results ) {
							if( !result ) return false;
						}
						return true;
					})
				);

			} ),
			switchMap( b => {

				if( !b ) return of(false);

				return this.questionService.deleteForm( formId );

			}),
			catchError( e => of(false) )
		);

	}


}

